// AI import popup: the matcher in an iframe over the CRM, with minimize /
// maximize / close. On Submit the matcher posts DONE_MESSAGE and we go to its page.
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import swal from 'sweetalert2';
import { FaWindowMinimize, FaWindowMaximize, FaWindowRestore, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';

const DONE_MESSAGE = 'encore-ai-import:done';

// Above the CRM's fixed buttons (scroll-to-top is 9999).
const Z_BACKDROP = 10000;
const Z_PANEL = 10001;
const Z_CONFIRM = 10100;

const headerButton = {
  background: 'transparent',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
};

// Pages call openAiImportPopup() below, not this. Bumping restoreKey re-maximizes it.
function AiImportDialog({ url, orderNumber, customerName, onClose, restoreKey }) {
  // 'normal' = big centred popup, 'maximized' = full screen, 'minimized' = bottom bar.
  const [mode, setMode] = useState('maximized');
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (restoreKey) setMode('maximized');
  }, [restoreKey]);

  const matcherOrigin = (() => {
    try { return new URL(url).origin; } catch (e) { return null; }
  })();

  // Import submitted: go to the page the matcher names. Trust only our iframe.
  useEffect(() => {
    const onMessage = (event) => {
      if (!matcherOrigin || event.origin !== matcherOrigin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const data = event.data || {};
      if (data.type !== DONE_MESSAGE) return;
      const path = typeof data.redirectPath === 'string' ? data.redirectPath : '';
      // Same-site paths only.
      if (path.startsWith('/') && !path.startsWith('//')) {
        window.location.href = path;
      } else {
        window.location.reload();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [matcherOrigin]);

  // No page scroll behind the popup unless minimized.
  useEffect(() => {
    if (mode === 'minimized') return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [mode]);

  const confirmClose = async () => {
    const result = await swal.fire({
      icon: 'warning',
      title: 'Close the AI import?',
      text: 'Anything not submitted yet will be lost.',
      showCancelButton: true,
      confirmButtonText: 'Close',
      cancelButtonText: 'Keep working',
      // App.scss pins swal to 9999 !important, so override it the same way.
      didOpen: () => { swal.getContainer().style.setProperty('z-index', String(Z_CONFIRM), 'important'); },
    });
    if (result.isConfirmed) onClose();
  };

  // Matcher in a new tab; the popup minimizes and keeps its work.
  const openInNewTab = () => {
    const tab = window.open(url, '_blank');
    if (tab) {
      tab.opener = null;
      setMode('minimized');
    }
  };

  const minimized = mode === 'minimized';
  const maximized = mode === 'maximized';

  const panelStyle = minimized
    ? { position: 'fixed', right: 16, bottom: 16, width: 420, height: 44 }
    : maximized
      ? { position: 'fixed', inset: 0, width: '100vw', height: '100vh' }
      : { position: 'fixed', top: '4vh', left: '3vw', width: '94vw', height: '92vh' };

  return ReactDOM.createPortal(
    <>
      {!minimized && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: Z_BACKDROP }} />
      )}
      <div
        style={{
          ...panelStyle,
          zIndex: Z_PANEL,
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: maximized ? 0 : 8,
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: 44,
            flex: '0 0 44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 8px 0 14px',
            background: '#1f2937',
            color: '#fff',
            cursor: minimized ? 'pointer' : 'default',
          }}
          onClick={minimized ? () => setMode('maximized') : undefined}
          title={minimized ? 'Maximize' : undefined}
        >
          <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            AI Drawing Import{orderNumber ? ` · ${orderNumber}` : ''}{customerName ? ` · ${customerName}` : ''}
          </span>
          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            {!minimized && (
              <button type="button" style={headerButton} title="Open in new tab" onClick={openInNewTab}>
                <FaExternalLinkAlt size={13} />
              </button>
            )}
            {!minimized && (
              <button type="button" style={headerButton} title="Minimize" onClick={() => setMode('minimized')}>
                <FaWindowMinimize size={13} />
              </button>
            )}
            {maximized ? (
              <button type="button" style={headerButton} title="Restore" onClick={() => setMode('normal')}>
                <FaWindowRestore size={13} />
              </button>
            ) : (
              <button type="button" style={headerButton} title="Maximize" onClick={() => setMode('maximized')}>
                <FaWindowMaximize size={13} />
              </button>
            )}
            <button type="button" style={headerButton} title="Close" onClick={confirmClose}>
              <FaTimes size={15} />
            </button>
          </div>
        </div>

        {/* Hidden, not removed, when minimized so the matcher keeps its state. */}
        <div style={{ position: 'relative', flex: 1, display: minimized ? 'none' : 'block' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>
              Loading AI drawing tool…
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={url}
            title="AI Drawing Import"
            onLoad={() => setLoading(false)}
            allow="clipboard-read; clipboard-write"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        </div>
      </div>
    </>,
    document.body
  );
}

// Own React root on <body>, so the popup survives tab and sidebar navigation.
let popupHost = null;
let popupRoot = null;
let popupState = null; // { url, orderNumber, customerName, restoreKey }
let logoutTimer = null;

function renderPopup() {
  popupRoot.render(
    <AiImportDialog
      url={popupState.url}
      orderNumber={popupState.orderNumber}
      customerName={popupState.customerName}
      restoreKey={popupState.restoreKey}
      onClose={closeAiImportPopup}
    />
  );
}

export function openAiImportPopup(url, orderNumber, customerName) {
  if (popupRoot) closeAiImportPopup();
  popupHost = document.createElement('div');
  document.body.appendChild(popupHost);
  popupRoot = createRoot(popupHost);
  popupState = { url, orderNumber, customerName, restoreKey: 0 };
  renderPopup();
  // Close on logout (token cleared).
  logoutTimer = window.setInterval(() => {
    if (!localStorage.getItem('token')) closeAiImportPopup();
  }, 2000);
}

// { orderNumber, customerName } of the open popup, or null.
export function getOpenAiImport() {
  return popupState ? { orderNumber: popupState.orderNumber, customerName: popupState.customerName } : null;
}

// Bring the popup back maximized, work kept.
export function restoreAiImportPopup() {
  if (!popupState) return;
  popupState = { ...popupState, restoreKey: popupState.restoreKey + 1 };
  renderPopup();
}

export function closeAiImportPopup() {
  if (logoutTimer) window.clearInterval(logoutTimer);
  logoutTimer = null;
  const root = popupRoot;
  const host = popupHost;
  popupRoot = null;
  popupHost = null;
  popupState = null;
  // Unmount after the current event, not during it.
  setTimeout(() => {
    if (root) root.unmount();
    if (host) host.remove();
  }, 0);
}
